import { useEffect, useState } from 'react';
import { ApolloClient, gql } from '@apollo/client';

import { clone } from '../../../constants';

export function useGetProducts(args: GetProductsArgs): GetProductsResponse {
	const { appKey, onlineStatus, apolloClient } = args;
	const [products, setProducts] = useState<Array<Product>>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<Error | null>(null);
	useEffect(() => {
		if (!apolloClient) {
			return;
		}
		if (!onlineStatus) {
			return;
		}
		getProducts({
			appKey,
			apolloClient,
			onlineStatus,
		})
			.then((p) => {
				setProducts(p);
				setIsLoading(false);
			})
			.catch((e) => {
				setError(e);
				setIsLoading(false);
			});
	}, [apolloClient, onlineStatus]);
	return {
		products,
		isLoading,
		error,
	};
}

interface GetProductsArgs {
	appKey: string;
	apolloClient?: ApolloClient<any>;
	onlineStatus: boolean | null;
}

async function getProducts(args: GetProductsRequest): Promise<Array<Product>> {
	const { apolloClient } = args;

	const result = await apolloClient.query({
		query: GET_PRODUCTS,
	});

	const data = clone(result.data);
	const products: Array<Product> = data.products.map(mapProducts);
	const pricesMap: Map<string, Array<Price>> = data.prices.reduce(
		reducePrices,
		new Map(),
	);

	const response = products
		.map((p) => {
			p.prices = pricesMap.get(p.id) || [];
			return p;
		})
		.filter((p) => p.prices.length);

	return response;
}

interface GetProductsRequest {
	appKey: string;
	apolloClient: ApolloClient<any>;
	onlineStatus: boolean | null;
}

function mapProducts(product: any): Product {
	const { id, active, name, description, images } = product;
	return {
		id,
		active,
		name,
		description,
		images,
		prices: [],
	};
}

function reducePrices(
	acc: Map<string, Array<Price>>,
	p: any,
): Map<string, Array<Price>> {
	const {
		id,
		active,
		currency,
		unitAmount,
		metadata,
		nickname,
		type,
		product,
		recurring,
	} = p;
	if (type !== 'recurring') {
		return acc;
	}
	const prices: Array<Price> = acc.get(product) || [];
	const price: Price = {
		id,
		active,
		currency,
		unitAmount,
		metadata,
		nickname,
		type,
		product,
		recurring:
			recurring === null
				? null
				: {
						interval: recurring.interval,
						intervalCount: recurring.intervalCount,
						meter: recurring.meter,
						trialPeriodDays: recurring.trialPeriodDays,
				  },
	};
	prices.push(price);
	acc.set(product, prices);
	return acc;
}

interface GetProductsResponse {
	products: Array<Product>;
	isLoading: boolean;
	error: Error | null;
}

export interface Product {
	id: string;
	active: boolean;
	description: string;
	name: string;
	images: Array<string>;
	prices: Array<Price>;
}

export interface Price {
	id: string;
	active: boolean;
	currency: string;
	metadata: any;
	nickname: string | null;
	type: 'one_time' | 'recurring';
	product: string | null;
	recurring: Recurring | null;
	unitAmount: number | null;
}

export interface Recurring {
	interval: 'day' | 'month' | 'week' | 'year';
	intervalCount: number;
	meter: string | null;
	trialPeriodDays: number | null;
}

const GET_PRODUCTS = gql`
	{
		products {
			id
			active
			description
			name
			images
		}

		prices {
			id
			active
			currency
			unitAmount
			metadata
			nickname
			type
			product
			recurring {
				interval
				intervalCount
				trialPeriodDays
			}
		}
	}
`;
